/**
 * App-level owner of the in-flight chat SSE stream.
 *
 * The stream lives above the router so navigating away from /chat does
 * NOT abort it. Without this hoisting, ChatPage.onCleanup → abortCtl.abort
 * kills the stream the moment the user clicks "dashboard", and the BE
 * persistence (which only fires at successful end-of-stream) never runs —
 * the assistant's reply is lost entirely, not just truncated.
 *
 * State is keyed by the thread that's actively streaming. When ChatPage
 * mounts on that thread, it reads the live turns from here; on any other
 * thread it falls back to its own BE-hydrated transcript.
 */
import {
  type Accessor,
  type Component,
  type JSX,
  createContext,
  createSignal,
  useContext,
} from "solid-js";

import type { ChatRequest } from "@/api/generated";

import { streamChat } from "./sseClient";
import { type ChatTurn, applyDeltaToTurn } from "./turn";

export interface ActiveChatStream {
  threadId: string;
  turns: ChatTurn[];
  streaming: boolean;
  asstTurnId: string;
}

export interface StartStreamParams {
  threadId: string;
  /** Full turn list to seed the stream view (existing transcript + new user + asst shell). */
  seedTurns: ChatTurn[];
  /** Id of the asst turn the deltas patch into. */
  asstTurnId: string;
  req: ChatRequest;
  token: string | undefined;
  onSettled?: () => void;
}

export interface ChatStreamContextValue {
  active: Accessor<ActiveChatStream | null>;
  start: (params: StartStreamParams) => void;
  stop: () => void;
  /** Drop the stored active stream for a thread (use after the FE has
   * re-hydrated from the BE — keeps active from going stale). */
  clear: (threadId: string) => void;
}

const ChatStreamContext = createContext<ChatStreamContextValue>();

export const ChatStreamProvider: Component<{ children: JSX.Element }> = (
  props,
) => {
  const [active, setActive] = createSignal<ActiveChatStream | null>(null);
  let abortCtl: AbortController | null = null;

  const patch = (
    threadId: string,
    turnId: string,
    fn: (t: ChatTurn) => ChatTurn,
  ) => {
    setActive((a) => {
      if (!a || a.threadId !== threadId) return a;
      return {
        ...a,
        turns: a.turns.map((t) => (t.id === turnId ? fn(t) : t)),
      };
    });
  };

  const start = ({
    threadId,
    seedTurns,
    asstTurnId,
    req,
    token,
    onSettled,
  }: StartStreamParams) => {
    abortCtl?.abort();
    abortCtl = new AbortController();
    const ctl = abortCtl;
    setActive({ threadId, turns: seedTurns, asstTurnId, streaming: true });

    void (async () => {
      try {
        for await (const chunk of streamChat(req, {
          token,
          signal: ctl.signal,
        })) {
          patch(threadId, asstTurnId, (t) => applyDeltaToTurn(t, chunk));
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        patch(threadId, asstTurnId, (t) => ({
          ...t,
          pending: false,
          error: msg,
        }));
      } finally {
        setActive((a) => {
          if (!a || a.threadId !== threadId) return a;
          return {
            ...a,
            streaming: false,
            turns: a.turns.map((t) =>
              t.id === asstTurnId ? { ...t, pending: false } : t,
            ),
          };
        });
        if (abortCtl === ctl) abortCtl = null;
        onSettled?.();
      }
    })();
  };

  const stop = () => {
    abortCtl?.abort();
  };

  const clear = (threadId: string) => {
    setActive((a) => (a && a.threadId === threadId ? null : a));
  };

  return (
    <ChatStreamContext.Provider value={{ active, start, stop, clear }}>
      {props.children}
    </ChatStreamContext.Provider>
  );
};

export function useChatStream(): ChatStreamContextValue {
  const ctx = useContext(ChatStreamContext);
  if (!ctx) {
    throw new Error("useChatStream must be inside <ChatStreamProvider>");
  }
  return ctx;
}
